import {
    Box,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import FormSection from "../../../forms/FormSection";
import ChartSkeleton from "./ChartSkeleton";
import {chartPalette} from "../../../themes/tokens";
import BoxesCountRenderer from "../../../utils/BoxesCountRenderer";
import {FulfillmentBar, FulfillmentCell, formatMoney, formatPercentage} from "./SalesTableFormatting";

const TABLE_BOX = { p: 2 };
const GROUP_COLORS = chartPalette;
const TOTAL_COLOR = "#8C7B68";
const MIX_BAR_HEIGHT = 22;

function TableBreadcrumb({ root, leaf, onExit }) {
    return (
        <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            <Link component="button" type="button" onClick={onExit} underline="hover">
                {root}
            </Link>
            {" › "}
            {leaf}
        </Typography>
    );
}

function MixBar({ mix, color }) {
    const cappedMix = Math.min(mix || 0, 100);
    return (
        <Box
            sx={{
                position: "relative",
                height: MIX_BAR_HEIGHT,
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
            }}
        >
            <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${cappedMix}%`, backgroundColor: color }} />
        </Box>
    );
}

function SalesByMixTable({ title, groupHeader, loaded, data, boxesMode, onGroupClick = null, breadcrumb = null }) {
    if (!loaded) {
        return (
            <FormSection title={title}>
                <ChartSkeleton />
            </FormSection>
        );
    }

    const rowsAreClickable = !!onGroupClick;
    const rowIsClickable = (row) => rowsAreClickable && row.canBeOpened;
    const valueHeader = boxesMode ? "Cajas" : "Ventas Netas";
    const inActiveUnit = (entry) => (boxesMode
        ? { group: entry.group, value: entry.netBoxes, renderedValue: BoxesCountRenderer(entry.netBoxes),
            mix: entry.boxesMix, fulfillment: entry.boxesFulfillment, projectedFulfillment: entry.boxesProjectedFulfillment,
            canBeOpened: entry.canBeOpened }
        : { group: entry.group, value: entry.netSales, renderedValue: formatMoney(entry.netSales),
            mix: entry.mix, fulfillment: entry.fulfillment, projectedFulfillment: entry.projectedFulfillment,
            canBeOpened: entry.canBeOpened });

    const rows = (data?.rows || [])
        .map(inActiveUnit)
        .sort((aRow, anotherRow) => (anotherRow.value || 0) - (aRow.value || 0));
    const total = data?.total ? inActiveUnit(data.total) : null;

    return (
        <FormSection title={title}>
            <Box sx={TABLE_BOX}>
                {breadcrumb && <TableBreadcrumb root={breadcrumb.root} leaf={breadcrumb.leaf} onExit={breadcrumb.onExit} />}
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{groupHeader}</TableCell>
                            <TableCell align="right">{valueHeader}</TableCell>
                            <TableCell align="right">% Mix</TableCell>
                            <TableCell sx={{ width: "30%" }}>% Mix / Cumplimiento</TableCell>
                            <TableCell align="center">Cumpl. vs Ppto.</TableCell>
                            <TableCell align="center">Proy. vs Ppto.</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow
                                key={row.group}
                                hover={rowIsClickable(row)}
                                onClick={rowIsClickable(row) ? () => onGroupClick(row.group) : undefined}
                                sx={rowIsClickable(row) ? { cursor: "pointer" } : undefined}
                            >
                                <TableCell>{row.group}</TableCell>
                                <TableCell align="right">{row.renderedValue}</TableCell>
                                <TableCell align="right">{formatPercentage(row.mix)}</TableCell>
                                <TableCell>
                                    <MixBar mix={row.mix} color={GROUP_COLORS[index % GROUP_COLORS.length]} />
                                </TableCell>
                                <TableCell align="center" />
                                <TableCell align="center" />
                            </TableRow>
                        ))}
                        {total && (
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    {total.renderedValue}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    {formatPercentage(total.mix)}
                                </TableCell>
                                <TableCell>
                                    <FulfillmentBar
                                        fulfillment={total.fulfillment}
                                        projectedFulfillment={total.projectedFulfillment}
                                        color={TOTAL_COLOR}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <FulfillmentCell value={total.fulfillment} />
                                </TableCell>
                                <TableCell align="center">
                                    <FulfillmentCell value={total.projectedFulfillment} />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </FormSection>
    );
}

export default SalesByMixTable;
